import {ReactNode, useCallback, useEffect, useRef, useState} from 'react';
import * as Sentry from '@sentry/react';
import {useNavigate} from 'react-router-dom';
import type {Area} from 'react-easy-crop';
import {useTranslation} from 'react-i18next';
import {
  AlertBox,
  cn,
  MobileScreenshotPayLoad,
  VideoConstraints,
  WebcamRefTypes,
} from '@chekin/guestapp-ui-kit';
import {IV_PHOTO_SOURCE} from '@chekin/core';
import {UPLOAD_PHOTO_STATES} from './constants';
import {ANALYTIC_EVENTS, Analytics} from '../../analytics';
import {useErrorModal} from '../../hooks';
import {usePhotoBoxStore} from './store';
import {getCroppedImage} from '../../utils/getCroppedImage';
import {Buttons} from './components/Buttons';
import {UploadPhoto} from './components/UploadPhoto';
import {TakePhoto} from './components/TakePhoto';
import {Wrapper} from './styled';

type PhotoBoxProps = {
  mobileHelperText?: ReactNode | string;
  mobileHelperTitle?: ReactNode | string;
  preloadedScreenshot?: string;
  placeholderImage?: string;
  onUploadPhoto?: (props: {image: string; source: IV_PHOTO_SOURCE}) => void;
  isUpload?: boolean;
  isOneSide?: boolean;
  roundedScanArea?: boolean;
  additionalConstraints?: VideoConstraints;
  layoutMode?: string;
  className?: string;
};
const PhotoBox = ({
  isUpload,
  onUploadPhoto,
  placeholderImage,
  additionalConstraints,
  mobileHelperText,
  mobileHelperTitle,
  roundedScanArea,
  layoutMode = 'default',
  className,
}: PhotoBoxProps) => {
  const webcamRef = useRef<WebcamRefTypes>(null);
  const uploadedImage = usePhotoBoxStore(state => state.uploadedImage);
  const setPhotoBoxUploadedImage = usePhotoBoxStore(state => state.setUploadedImage);
  const resetPhotoBoxUploadedImage = usePhotoBoxStore(state => state.resetUploadedImage);
  const [disabled, setDisabled] = useState(false);
  const {displayError, ErrorModal} = useErrorModal();
  const navigate = useNavigate();
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const croppedAreaPixelsRef = useRef<Area | null>(null);
  const {t} = useTranslation('identity-verification');

  const uploadPhotoState = (() => {
    if (isUpload && !uploadedImage) {
      return UPLOAD_PHOTO_STATES.upload;
    } else if (isUpload && uploadedImage && !croppedImage) {
      return UPLOAD_PHOTO_STATES.crop;
    } else if (isUpload && croppedImage) {
      return UPLOAD_PHOTO_STATES.confirmUpload;
    } else if (uploadedImage && !isUpload) {
      return UPLOAD_PHOTO_STATES.confirmTaking;
    } else {
      return UPLOAD_PHOTO_STATES.take;
    }
  })();

  useEffect(
    function resetImageWhenLeavePage() {
      return () => {
        resetPhotoBoxUploadedImage();
      };
    },
    [resetPhotoBoxUploadedImage],
  );

  const handleTakePhoto = useCallback(
    async (payload: MobileScreenshotPayLoad) => {
      try {
        Analytics.track(ANALYTIC_EVENTS.ivPhotoTaken, {
          'Used camera': payload.isNative ? 'native' : 'app',
        });

        const image = payload.isNative
          ? await payload.photo
          : await webcamRef?.current?.takePhoto();

        if (image)
          onUploadPhoto?.({
            image,
            source: payload.isNative
              ? IV_PHOTO_SOURCE.nativeCamera
              : IV_PHOTO_SOURCE.appCamera,
          });
      } catch (err) {
        Analytics.track(ANALYTIC_EVENTS.ivTakingPhotoError, {
          'Error message': (err as Error)?.message,
        });
        Sentry.captureException(err);
        displayError(err);
      }
    },
    [displayError, onUploadPhoto],
  );

  const handleUploadPhoto = useCallback(
    (image: string) => {
      setPhotoBoxUploadedImage(image);
    },
    [setPhotoBoxUploadedImage],
  );

  const handleCropComplete = useCallback((croppedAreaPixels: Area) => {
    croppedAreaPixelsRef.current = croppedAreaPixels;
  }, []);

  const handleConfirmCrop = useCallback(async () => {
    if (!uploadedImage || !croppedAreaPixelsRef.current) return;

    try {
      const cropped = await getCroppedImage(uploadedImage, croppedAreaPixelsRef.current);
      setCroppedImage(cropped);
    } catch (err) {
      Sentry.captureException(err);
      displayError(err);
    }
  }, [uploadedImage, displayError]);

  const handleChangePhoto = useCallback(() => {
    resetPhotoBoxUploadedImage();
    setCroppedImage(null);
    croppedAreaPixelsRef.current = null;
    Analytics.track(ANALYTIC_EVENTS.ivResetPhoto);
  }, [resetPhotoBoxUploadedImage]);

  const handleFinishUploadPhoto = useCallback(() => {
    const imageToUpload = croppedImage ?? uploadedImage;

    if (!imageToUpload) return;

    Analytics.track(ANALYTIC_EVENTS.ivPhotoUploaded);
    onUploadPhoto?.({image: imageToUpload, source: IV_PHOTO_SOURCE.external});
  }, [croppedImage, onUploadPhoto, uploadedImage]);

  const getContent = useCallback(() => {
    if (isUpload) {
      return (
        <UploadPhoto
          onUpload={handleUploadPhoto}
          onDisabled={setDisabled}
          image={uploadedImage}
          croppedImage={croppedImage}
          isCropping={uploadPhotoState === UPLOAD_PHOTO_STATES.crop}
          onCropComplete={handleCropComplete}
        />
      );
    }

    return (
      <TakePhoto
        ref={webcamRef}
        disabled={disabled}
        onDisabled={setDisabled}
        roundedScanArea={roundedScanArea}
        additionalConstraints={additionalConstraints}
        onScreenshot={handleTakePhoto}
        placeholderImage={placeholderImage}
        mobileHelperText={mobileHelperText}
        mobileHelperTitle={mobileHelperTitle}
        mirrored={roundedScanArea}
        layoutMode={layoutMode}
        onBack={() => navigate(-1)}
      />
    );
  }, [
    isUpload,
    navigate,
    layoutMode,
    roundedScanArea,
    additionalConstraints,
    handleTakePhoto,
    placeholderImage,
    mobileHelperText,
    mobileHelperTitle,
    handleUploadPhoto,
    uploadedImage,
    setDisabled,
    disabled,
    croppedImage,
    uploadPhotoState,
    handleCropComplete,
  ]);

  const isCropping = uploadPhotoState === UPLOAD_PHOTO_STATES.crop;

  return (
    <Wrapper className={cn('photo-box', className)}>
      {isCropping && (
        <AlertBox
          className="mb-4 flex items-start gap-2 text-left max-w-[420px]"
          variant="primary"
        >
          <span className="text-sm leading-5">
            {t('drag_or_zoom_center_your_document_in_the_frame')}
          </span>
        </AlertBox>
      )}
      {getContent()}
      <Buttons
        state={uploadPhotoState}
        onCapture={() => {
          handleTakePhoto({isNative: false});
        }}
        onChange={handleChangePhoto}
        onConfirmCrop={handleConfirmCrop}
        disabled={disabled}
        onFinishUploadPhoto={handleFinishUploadPhoto}
      />
      <ErrorModal />
    </Wrapper>
  );
};

export {PhotoBox};
